import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from "react-toastify";
import { registerPatient, updatePatient, getPatientByUserId } from "../../services/patientService";
import { DEV_PATIENT_OTP } from "../../services/otpService";
import { registerPortalUser, updatePortalUser } from "../../services/userManagementService";
import { useAuth } from "../../context/AuthContext";
import {
  bindPatientToUser,
  patientRecordMatchesUser,
  getBookingOrganizationId,
  applyPatientBookingOrganization,
  normalizeOrganizationId,
} from "../../utils/patientPortalSession";

const BLUE = "#0B78D0";
const BLUE_DARK = "#0866B3";
const TEXT_DARK = "#1A1A1A";
const TEXT_GREY = "#6B7280";
const BORDER = "#E5E7EB";
const PAGE_BG = "#F3F4F6";

const capitalizeName = (inputValue) => {
  if (!inputValue) return "";
  return inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
};

export const isPatientProfileIncomplete = (patient) => {
  if (!patient?.id) return true;
  const name = String(patient.name || "").trim();
  return !name || !patient.gender || !patient.dob;
};

const StepDot = ({ active, done, index }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 72 }}>
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: "50%",
        backgroundColor: active || done ? BLUE : "#D1D5DB",
        mb: 0.8,
      }}
    />
    <Typography sx={{ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? TEXT_DARK : TEXT_GREY }}>
      Step {index}
    </Typography>
  </Box>
);

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
  },
};

const PatientAddProfile = ({
  user,
  existingPatient,
  verifiedMobile = "",
  organizationId = "",
  onCompleted,
  onBack,
}) => {
  const { login, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [values, setValues] = useState({
    name: existingPatient?.name || "",
    gender: existingPatient?.gender || "",
    dob: existingPatient?.dob ? dayjs(existingPatient.dob) : null,
    email: existingPatient?.email || "",
    address: existingPatient?.address || "",
    aadhar: existingPatient?.aadhar || "",
    mobile: existingPatient?.mobile || verifiedMobile || user?.username || "",
  });

  const setField = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const markTouched = (key) => {
    setTouched((current) => ({ ...current, [key]: true }));
  };

  const nameError =
    touched.name && String(values.name).trim().length < 2
      ? "Name should be of minimum 2 characters length"
      : "";
  const genderError = touched.gender && !values.gender ? "Gender is required" : "";
  const dobError =
    touched.dob && (!values.dob || !dayjs(values.dob).isValid())
      ? "DOB is required"
      : values.dob && dayjs(values.dob).isAfter(dayjs(), "day")
        ? "DOB cannot be in the future"
        : "";
  const addressError =
    touched.address && !String(values.address).trim() ? "Address is required" : "";
  const aadharError =
    values.aadhar && !/^\d{12}$/.test(values.aadhar)
      ? "Aadhar should be a 12-digit number"
      : "";
  const emailError =
    values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)
      ? "Enter a valid email"
      : "";
  const mobileError =
    touched.mobile && !/^\d{10}$/.test(String(values.mobile || ""))
      ? "Contact Number should be a 10-digit number"
      : "";

  const step1Valid =
    String(values.name).trim().length >= 2 &&
    Boolean(values.gender) &&
    Boolean(values.dob) &&
    dayjs(values.dob).isValid() &&
    !dayjs(values.dob).isAfter(dayjs(), "day");

  const step2Valid =
    /^\d{10}$/.test(String(values.mobile || "")) &&
    Boolean(String(values.address).trim()) &&
    !aadharError &&
    !emailError;

  const fullName = useMemo(
    () => String(values.name || "").replace(/\s+/g, " ").trim(),
    [values.name]
  );

  const goNext = () => {
    if (step === 1) {
      setTouched((current) => ({ ...current, name: true, gender: true, dob: true }));
      if (!step1Valid) return;
      setStep(2);
      return;
    }
    if (step === 2) {
      setTouched((current) => ({ ...current, mobile: true, address: true }));
      if (!step2Valid) return;
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const mobileNumber = String(values.mobile || verifiedMobile || "").trim();
      if (!/^\d{10}$/.test(mobileNumber)) {
        toast.error("A valid 10-digit mobile number is required.");
        return;
      }

      const bookingOrganizationId =
        normalizeOrganizationId(organizationId) || getBookingOrganizationId();

      let loggedInUser = user;

      if (!loggedInUser?.id) {
        try {
          await registerPortalUser({
            fullName,
            mobile: mobileNumber,
            password: DEV_PATIENT_OTP,
            role: "patient",
            ...(bookingOrganizationId ? { organizationId: bookingOrganizationId } : {}),
          });
        } catch (registerError) {
          console.warn("Portal user may already exist:", registerError);
        }

        loggedInUser = await login(mobileNumber, DEV_PATIENT_OTP, null);
      }

      if (!loggedInUser?.id) {
        toast.error("Unable to sign in after creating the account.");
        return;
      }

      const loginMobile = String(
        loggedInUser.mobileNumber || loggedInUser.username || ""
      )
        .replace(/\D/g, "")
        .slice(-10);

      if (loginMobile && loginMobile !== mobileNumber) {
        logout();
        toast.error("Signed into a different account. Please try again with this mobile number.");
        return;
      }

      if (bookingOrganizationId) {
        loggedInUser = applyPatientBookingOrganization(
          loggedInUser,
          bookingOrganizationId
        );
        try {
          await updatePortalUser({
            id: loggedInUser.id,
            full_name: fullName,
            user_pwd: DEV_PATIENT_OTP,
            user_role: "patient",
            email: values.email || "",
            department: "",
            mobile: mobileNumber,
            organization_id: bookingOrganizationId,
          });
        } catch (orgUpdateError) {
          console.warn("Could not persist hospital organization on portal user:", orgUpdateError);
        }
      }

      const payload = {
        name: fullName,
        mobile: mobileNumber,
        email: values.email || null,
        aadhar: values.aadhar || null,
        address: values.address || "",
        dob: dayjs(values.dob).format("YYYY-MM-DD"),
        gender: values.gender,
        patientType: "OUT",
        userId: loggedInUser.id,
      };

      let patientRecord = existingPatient;
      const shouldCreateInHospitalTable =
        Boolean(bookingOrganizationId) && !existingPatient?.id;

      if (
        !shouldCreateInHospitalTable &&
        !patientRecordMatchesUser(patientRecord, loggedInUser.id, mobileNumber)
      ) {
        patientRecord = null;
        try {
          patientRecord = await getPatientByUserId(loggedInUser.id, {
            mobile: mobileNumber,
            organizationId: bookingOrganizationId,
          });
        } catch (lookupError) {
          patientRecord = null;
        }
      }

      if (
        shouldCreateInHospitalTable ||
        !patientRecordMatchesUser(patientRecord, loggedInUser.id, mobileNumber)
      ) {
        patientRecord = null;
      }

      let savedPatient = null;
      if (patientRecord?.id) {
        savedPatient = await updatePatient(patientRecord.id, payload);
      } else {
        savedPatient = await registerPatient(payload, {
          createUserAccount: false,
          portalPassword: DEV_PATIENT_OTP,
          organizationId: bookingOrganizationId || undefined,
        });
      }

      const savedId = savedPatient?.id || savedPatient?.patientId || patientRecord?.id;
      bindPatientToUser(loggedInUser.id, savedId);

      toast.success("Profile created successfully.");
      onCompleted?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save profile. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minHeight: "100vh", backgroundColor: PAGE_BG, py: { xs: 2, md: 4 }, px: 2 }}>
        <Box sx={{ maxWidth: 640, mx: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
            {onBack ? (
              <IconButton onClick={onBack} sx={{ color: TEXT_DARK }}>
                <ArrowBackIcon />
              </IconButton>
            ) : null}
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: TEXT_DARK }}>
              Add Profile
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: "12px",
              boxShadow: "0 8px 30px rgba(15, 23, 42, 0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", mb: 4 }}>
              <StepDot index={1} active={step === 1} done={step > 1} />
              <Box sx={{ flex: 1, height: 2, bgcolor: step > 1 ? BLUE : "#E5E7EB", mt: 0.7 }} />
              <StepDot index={2} active={step === 2} done={step > 2} />
              <Box sx={{ flex: 1, height: 2, bgcolor: step > 2 ? BLUE : "#E5E7EB", mt: 0.7 }} />
              <StepDot index={3} active={step === 3} done={false} />
            </Box>

            {step === 1 && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Name"
                    value={values.name}
                    onChange={(e) => setField("name", capitalizeName(e.target.value))}
                    onBlur={() => markTouched("name")}
                    error={Boolean(nameError)}
                    helperText={nameError}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    select
                    fullWidth
                    size="small"
                    label="Gender"
                    value={values.gender}
                    onChange={(e) => setField("gender", e.target.value)}
                    onBlur={() => markTouched("gender")}
                    error={Boolean(genderError)}
                    helperText={genderError}
                    sx={fieldSx}
                    SelectProps={{
                      MenuProps: {
                        disablePortal: true,
                        disableScrollLock: true,
                        PaperProps: {
                          sx: {
                            zIndex: 2100,
                            maxHeight: 240,
                          },
                        },
                      },
                    }}
                  >
                    {["Male", "Female"].map((gender) => (
                      <MenuItem key={gender} value={gender}>
                        {gender}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="DOB"
                    format="YYYY-MM-DD"
                    value={values.dob}
                    onChange={(value) => {
                      setField("dob", value);
                      markTouched("dob");
                    }}
                    minDate={dayjs().subtract(100, "year")}
                    maxDate={dayjs()}
                    openTo="year"
                    views={["year", "month", "day"]}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        size: "small",
                        error: Boolean(dobError),
                        helperText: dobError || "Open calendar to select date",
                        sx: fieldSx,
                        onBlur: () => markTouched("dob"),
                      },
                      popper: { sx: { zIndex: 2100 } },
                      dialog: { sx: { zIndex: 2100 } },
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {step === 2 && (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Contact Number"
                    value={values.mobile}
                    onChange={(e) =>
                      setField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                    onBlur={() => markTouched("mobile")}
                    inputProps={{ maxLength: 10 }}
                    error={Boolean(mobileError)}
                    helperText={mobileError || "10-digit mobile number"}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    value={values.email}
                    onChange={(e) => setField("email", e.target.value)}
                    error={Boolean(emailError)}
                    helperText={emailError}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Aadhar"
                    value={values.aadhar}
                    onChange={(e) =>
                      setField("aadhar", e.target.value.replace(/\D/g, "").slice(0, 12))
                    }
                    inputProps={{ maxLength: 12 }}
                    error={Boolean(aadharError)}
                    helperText={aadharError}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Address"
                    value={values.address}
                    onChange={(e) => setField("address", e.target.value)}
                    onBlur={() => markTouched("address")}
                    multiline
                    minRows={2}
                    error={Boolean(addressError)}
                    helperText={addressError}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            )}

            {step === 3 && (
              <Box sx={{ color: TEXT_DARK }}>
                <Typography sx={{ fontWeight: 800, mb: 2 }}>Confirm your details</Typography>
                {[
                  ["Name", fullName],
                  ["Gender", values.gender],
                  ["DOB", values.dob ? dayjs(values.dob).format("YYYY-MM-DD") : ""],
                  ["Contact Number", values.mobile],
                  ["Email", values.email || "—"],
                  ["Aadhar", values.aadhar || "—"],
                  ["Address", values.address || "—"],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.8,
                      borderBottom: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography sx={{ color: TEXT_GREY, fontSize: 14 }}>{label}</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, textAlign: "right", pl: 2 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1.5, mt: 4 }}>
              {step > 1 && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setStep((current) => current - 1)}
                  sx={{
                    minHeight: 48,
                    borderColor: BORDER,
                    color: TEXT_DARK,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                fullWidth
                variant="contained"
                disabled={
                  submitting ||
                  (step === 1 && !step1Valid) ||
                  (step === 2 && !step2Valid)
                }
                onClick={step === 3 ? handleSubmit : goNext}
                sx={{
                  minHeight: 48,
                  backgroundColor: BLUE,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 16,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: BLUE_DARK, boxShadow: "none" },
                  "&.Mui-disabled": { backgroundColor: "#D1D5DB", color: "#fff" },
                }}
              >
                {submitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : step === 3 ? (
                  "Create profile"
                ) : (
                  "Next"
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default PatientAddProfile;
